import { Eye, PenSquare, AlertTriangle } from 'lucide-react';
import Button from './ui/Button';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { toast } from 'sonner';

interface PagePublishBannerProps {
  isPublished: boolean;
  onPublish: () => void;
  onUnpublish: () => void;
  onPreview: () => void;
  pathValue?: string;
  onPathChange?: (val: string) => void;
  onPathSave?: () => void;
  pathError?: string | null;
  isSavingPath?: boolean;
}

export default function PagePublishBanner({
  isPublished,
  onPublish,
  onUnpublish,
  onPreview,
  pathValue,
  onPathChange,
  onPathSave,
  pathError,
  isSavingPath
}: PagePublishBannerProps) {
  const { t } = useTranslation();
  const [isEditingPath, setIsEditingPath] = useState(false);

  const handleGuarded = (action: () => void) => {
    if (!pathValue || pathValue.trim().length === 0) {
      toast.error('Save your path first');
      return;
    }
    action();
  };
  return (
    <div className="bg-gray-100 border-b border-gray-200">
      {/* This row did not wrap, so on a 390px phone the Preview/Publish buttons
          set the width of the whole creator dashboard and every page scrolled
          sideways. min-w-0 matters as much as flex-wrap: a flex child defaults
          to min-width:auto and refuses to shrink below its content. sportfund
          fixed this as TSF-233; this product never got it. */}
      <div className="px-4 py-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 min-w-0">
          <div className="text-sm flex items-center gap-2">
            {isPublished ? (
              <span className="text-gray-600">{t('creator.mypage.published')}</span>
            ) : (
              <>
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <span className="text-red-700">{t('creator.mypage.notPublished')}</span>
              </>
            )}
          </div>
          {typeof pathValue !== 'undefined' && typeof onPathChange === 'function' && (
            <div className="flex items-center gap-2">
              {isEditingPath ? (
                <>
                  <span className="text-sm text-gray-500 hidden sm:inline">{window.location.origin}/mission/</span>
                  <input
                    type="text"
                    value={pathValue}
                    onChange={(e) => onPathChange(e.target.value)}
                    className="px-2 py-1 border rounded-md text-sm w-48"
                    placeholder="your-url"
                  />
                  {typeof onPathSave === 'function' && (
                    <Button
                      variant="outline"
                      onClick={() => { onPathSave(); setIsEditingPath(false); }}
                      disabled={!!isSavingPath}
                    >
                      {isSavingPath ? t('dashboard.common.saving') : t('dashboard.common.save')}
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => setIsEditingPath(false)}>{t('dashboard.common.cancel') || 'Cancel'}</Button>
                </>
              ) : (
                <>
                  <div className="text-sm text-gray-700">
                    <span className="text-gray-500 hidden sm:inline">{window.location.origin}/mission/</span>
                    <span>{pathValue || '—'}</span>
                  </div>
                  <button
                    type="button"
                    className="text-gray-500 hover:text-gray-700"
                    onClick={() => setIsEditingPath(true)}
                    aria-label="Edit URL"
                  >
                    <PenSquare className="h-4 w-4" />
                  </button>
                </>
              )}
            </div>
          )}
          {pathError && <div className="text-xs text-red-600">{pathError}</div>}
        </div>
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            onClick={() => handleGuarded(onPreview)}
            disabled={!pathValue || pathValue.trim().length === 0}
            className="flex items-center space-x-2"
          >
            <Eye className="h-4 w-4" />
            <span>{t('creator.mypage.preview')}</span>
          </Button>
          <Button
            variant={isPublished ? 'outline' : 'primary'}
            onClick={() => handleGuarded(isPublished ? onUnpublish : onPublish)}
            disabled={!pathValue || pathValue.trim().length === 0}
          >
            {isPublished ? t('creator.mypage.unpublish') : t('creator.mypage.publish')}
          </Button>
        </div>
      </div>
    </div>
  );
}